import { HighlightedRequest } from '@components/highlightedRequest/highlightedRequest';
import { StaticMap } from '@components/static-map/static-map';
import { useIsVenue } from '@hooks/use-is-venue';
import { useLiteStorefront } from '@hooks/use-lite-storefront';
import { useAppSelector } from '@redux/hooks';
import { H3 } from '@xo-union/tk-ui-typography';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import React, { type FC } from 'react';
import type { Decorated } from 'types/vendor';
import {
	travelAvailabilityAtom,
	travelRangeAtom,
} from '../../jotai/storefront/organization';
import type { NavItemProps } from '../../pages/Storefront/containers/nav/types';
import { LocationHighlights } from './components/location-highlights/location-highlights';
import Styles from './location.scss';

interface LocationProps extends NavItemProps {
	forcedTitle?: null | string;
}

export const Location: FC<LocationProps> = ({ forcedTitle = null }) => {
	const vendor = useAppSelector((state) => state.vendor.vendor as Decorated);
	const isVenue = useIsVenue();
	const isLiteStorefront = useLiteStorefront();
	const title = isVenue ? 'Location' : 'Service area';
	const travelAvailability = useAtomValue(travelAvailabilityAtom);
	const travelRange = useAtomValue(travelRangeAtom);
	const isTravelPossible = travelRange || travelAvailability;

	const isAddressPublic = vendor.address.isPublic;
	const hasHighlights = vendor.serviceArea || isTravelPossible;

	if (!isAddressPublic && !isTravelPossible && !isVenue) {
		return null;
	}

	return (
		<div
			className={classNames(Styles.container, {
				[Styles.simplifiedContainer]: !isAddressPublic,
			})}
			id="location"
		>
			<H3 className={Styles.title}>{forcedTitle ?? title}</H3>
			{isAddressPublic && <StaticMap address={vendor.address} />}
			<div className={Styles.bottomContent}>
				{isLiteStorefront && (isVenue || !hasHighlights) && (
					<HighlightedRequest
						messageText="Want to book a visit?"
						buttonText="Start a conversation"
						initiator="Location CTA"
						hideIfLead={true}
					/>
				)}
				{!isVenue && hasHighlights && (
					<LocationHighlights
						serviceArea={vendor.serviceArea}
						travelsUpTo={travelAvailability}
						travelRange={travelRange}
						isAddressPublic={isAddressPublic}
					/>
				)}
			</div>
		</div>
	);
};
